import { currentUserQuery } from "@common/api/users"
import { useCloseWorkPackageMutation } from "@common/api/work-packages"
import { ConfirmDialog } from "@common/components/shared/confirm-dialog"
import { hasPermission } from "@common/lib/permissions"
import { handleError } from "@common/utils"
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router"
import useCustomToast from "@/hooks/use-custom-toast"

export const Route = createFileRoute("/_layout/work-orders/$workOrderId/close")(
  {
    beforeLoad: async ({ context: { queryClient }, params, search }) => {
      const me = await queryClient.ensureQueryData(currentUserQuery())
      if (!hasPermission(me, "work_package:close")) {
        throw redirect({ to: "/work-orders/$workOrderId", params, search })
      }
    },
    component: CloseWorkOrderDialog,
  },
)

function CloseWorkOrderDialog(): React.ReactElement {
  const { workOrderId } = Route.useParams()
  const navigate = useNavigate()
  const showToast = useCustomToast()
  const mutation = useCloseWorkPackageMutation()

  const back = () =>
    navigate({
      to: "/work-orders/$workOrderId",
      params: { workOrderId },
      search: true,
    })

  const confirm = () =>
    mutation.mutate(
      { work_package_id: workOrderId },
      {
        onSuccess: () => {
          showToast("Closed", "Work order closed.", "success")
          back()
        },
        onError: (error) => handleError(error, showToast),
      },
    )

  return (
    <ConfirmDialog
      title="Close this work order?"
      description="Closing is final. A closed work order can't be reopened or changed."
      action="Close work order"
      pendingAction="Closing…"
      cancelLabel="Keep it open"
      tone="default"
      pending={mutation.isPending}
      onConfirm={confirm}
      onCancel={back}
    />
  )
}
